import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { redirectToCandleCheckout } from "@util/stripe-checkout";
import { getRootDomain } from "@util/domain";
import { Memorial } from "@models/Memorial";
import { fetchMemorial } from "@services/memorialService";
import ConfirmationPopup from "@components/modals/ConfirmationPopup/ConfirmationPopup";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { formatDate } from "@util/string";
import styles from "./PurchaseCandlePage.module.css";

type CandleColour = "amethyst" | "burgundy" | "forest" | "midnight" | "rose gold";
type CandleType = "original" | "deluxe";

const VALID_CANDLE_COLOURS: CandleColour[] = ["amethyst", "burgundy", "forest", "midnight", "rose gold"];
const VALID_CANDLE_TYPES: CandleType[] = ["original", "deluxe"];

// Display labels
const CANDLE_COLOUR_LABELS: Record<CandleColour, string> = {
    amethyst: "Amethyst",
    burgundy: "Burgundy",
    forest: "Forest",
    midnight: "Midnight",
    "rose gold": "Rose Gold",
};

const CANDLE_COLOUR_IMAGES: Record<CandleColour, string> = {
    amethyst: "/assets/images/candle-colours/amethyst.webp",
    burgundy: "/assets/images/candle-colours/burgundy.webp",
    forest: "/assets/images/candle-colours/forest.webp",
    midnight: "/assets/images/candle-colours/midnight.webp",
    "rose gold": "/assets/images/candle-colours/rose-gold.webp",
};

const CANDLE_TYPE_CONFIG: {
    id: CandleType;
    name: string;
    subtitle: string;
    burnTime: string;
    weight: string;
}[] = [
    {
        id: "original",
        name: "Original",
        subtitle: "Perfect everyday remembrance size.",
        burnTime: "Approx. 30 hours burn time",
        weight: "190g",
    },
    {
        id: "deluxe",
        name: "Deluxe",
        subtitle: "Larger, longer-lasting tribute.",
        burnTime: "Approx. 60 hours burn time",
        weight: "420g",
    },
];

// Optional pricing mapping – wire to your real env vars.
const CANDLE_PRICING: Record<CandleType, number> = {
    original: Number(process.env.REACT_APP_CANDLE_PRICE_ORIGINAL ?? 0),
    deluxe: Number(process.env.REACT_APP_CANDLE_PRICE_DELUXE ?? 0),
};

const PurchaseCandlePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const memorialId = searchParams.get("id");
    const [memorial, setMemorial] = useState<Memorial>();
    const [showMissingDatesPopup, setShowMissingDatesPopup] = useState(false);

    const [selectedType, setSelectedType] = useState<CandleType>("deluxe");
    const [selectedColour, setSelectedColour] = useState<CandleColour>("amethyst");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!memorialId) {
                    throw new Error("No ID.");
                }
                const data = await fetchMemorial(memorialId);
                if (!data) {
                    throw new Error("Memorial not found.");
                }
                setMemorial(data);
                setLoading(false);
            } catch (err) {
                setError("Failed to fetch memorial information.");
            } finally {
                setLoading(false);
            }
        };
        void fetchData();
    }, [memorialId]);

    const handlePurchase = async () => {
        try {
            if (!memorialId) {
                throw new Error("Memorial ID is required for candle purchase.");
            }
            await redirectToCandleCheckout(selectedType, selectedColour, memorialId);
        } catch (e) {
            // eslint-disable-next-line no-alert
            alert("Error purchasing candle. Please try again.");
        }
    };

    const handlePurchaseClick = () => {
        if (!hasDates) {
            setShowMissingDatesPopup(true);
            return;
        }
        void handlePurchase();
    };

    const price = CANDLE_PRICING[selectedType];

    const hasDates = (memorial?.birthDate || memorial?.birthYear) && (memorial?.deathDate || memorial?.deathYear);

    if (loading || error) {
        return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
    }

    return (
        <div className={`${commonStyles.container} ${styles.pageWrapper}`}>
            {/* Hero */}
            <section className={styles.heroSection}>
                <h1 className={styles.heroTitle}>Light a Candle to Remember a Loved One</h1>
                <p className={styles.heroSubtitle}>
                    Create a personalised forever candle, linked to their online memorial.
                </p>
            </section>

            {/* Intro section similar to marketing page */}
            <section className={styles.infoSection}>
                <div className={styles.infoImageWrapper}>
                    <img
                        src="/assets/images/forever-candle.jpg"
                        alt="Forever Candle Example"
                        className={styles.infoImage}
                    />
                </div>
                <div className={styles.infoContent}>
                    <h2>A Candle That Keeps Their Memory Alive</h2>
                    <p>
                        Each Forever Candle is engraved with a personalised QR code that links directly to the
                        memorial page you create — giving family and friends a simple way to remember, reflect,
                        and reconnect anytime.
                    </p>
                </div>
            </section>

            {/* Forever Jar Section */}
            <section className={`${styles.infoSection} ${styles.infoSectionReversed}`}>
                <div className={styles.infoContent}>
                    <h2>A Candle Designed to Last a Lifetime</h2>
                    <p>
                        Each candle is housed in a premium reusable Forever Jar — designed to be kept long after
                        the wax has finished. Instead of replacing the whole candle, simply swap in a refill and
                        continue the tribute without waste.
                    </p>
                    <p>
                        Our Forever Jars are crafted in partnership with
                        {" "}
                        <a
                            href="https://lunaire.au/?ref=ohgklwdj"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.partnerLink}
                        >
                            Lunaire
                        </a>
                        , known for their beautifully engineered vessels that are built to last a lifetime.
                    </p>
                    <p>
                        One timeless jar. Countless refills. A lasting way to honour their memory.
                    </p>
                </div>
                <div className={styles.infoImageWrapper}>
                    <img
                        src="/assets/images/candle-refill.jpg"
                        alt="Forever Candle Refills"
                        className={styles.infoImage}
                    />
                </div>
            </section>

            {/* Size section */}
            <section className={styles.selectorSection}>
                <h2 className={styles.sectionTitle}>1. Choose Candle Size</h2>
                <p className={styles.sectionSubtitle}>
                    Pick the size that best suits the space where your candle will be displayed.
                </p>
                <div className={styles.selectorGrid}>
                    {CANDLE_TYPE_CONFIG.map((type) => {
                        const isSelected = selectedType === type.id;
                        return (
                            <button
                                key={type.id}
                                type="button"
                                className={`${styles.optionCard} ${
                                    isSelected ? styles.optionCardSelected : ""
                                }`}
                                onClick={() => setSelectedType(type.id)}
                            >
                                <div className={styles.optionHeader}>
                                    <span className={styles.optionTitle}>{type.name}</span>
                                    <span className={styles.optionTag}>{type.weight}</span>
                                </div>
                                <p className={styles.optionSubtitle}>{type.subtitle}</p>
                                <ul className={styles.optionDetails}>
                                    <li>{type.burnTime}</li>
                                    <li>Engraving linked to memorial</li>
                                    <li>Premium soy wax blend</li>
                                </ul>
                                {price > 0 && (
                                    <div className={styles.optionPriceRow}>
                                        {/* <span className={styles.optionPriceLabel}>From</span> */}
                                        <span className={styles.optionPriceValue}>
                                            $
                                            {CANDLE_PRICING[type.id]}
                                        </span>
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
                <p className={styles.faqLinkCentered}>
                    <a href={`${getRootDomain()}/forever-candle-faq`} target="_blank" rel="noreferrer">See FAQs →</a>
                </p>
            </section>

            {/* Colour section */}
            <section className={styles.selectorSection}>
                <h2 className={styles.sectionTitle}>2. Choose Candle Colour</h2>
                <p className={styles.sectionSubtitle}>
                    Select the finish that best reflects their personality or fits your space. All colours include the same
                    engraving and QR code.
                </p>
                <div className={styles.colourGrid}>
                    {VALID_CANDLE_COLOURS.map((colour) => {
                        const isSelected = selectedColour === colour;
                        return (
                            <button
                                key={colour}
                                type="button"
                                className={`${styles.colourCard} ${
                                    isSelected ? styles.optionCardSelected : ""
                                }`}
                                onClick={() => setSelectedColour(colour)}
                            >
                                <div className={styles.colourImageWrapper}>
                                    <img
                                        src={CANDLE_COLOUR_IMAGES[colour]}
                                        alt={`${CANDLE_COLOUR_LABELS[colour]} candle`}
                                        className={styles.colourImage}
                                    />
                                </div>
                                <span className={styles.colourLabel}>{CANDLE_COLOUR_LABELS[colour]}</span>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Summary & price */}
            <section className={styles.summarySection}>
                <div className={styles.summaryCard}>
                    <h2 className={styles.summaryTitle}>Your Selection</h2>
                    <p className={styles.summaryLine}>
                        <strong>
                            {`${CANDLE_TYPE_CONFIG.find((t) => t.id === selectedType)?.name}
                            Forever Jar (${CANDLE_COLOUR_LABELS[selectedColour]})
                            + Unscented Candle`}
                        </strong>
                    </p>
                    <p className={styles.summaryLine}>
                        <strong>
                            Engraving:
                            {" "}
                            {`${memorial?.firstName || ""} ${memorial?.lastName || ""} ${hasDates ? (`(${memorial?.birthDate ? formatDate(memorial.birthDate) : (memorial?.birthYear || "??")} - ${(memorial?.deathDate ? formatDate(memorial.deathDate) : memorial?.deathYear) || "??"})`) : ""} + QR Code`}
                        </strong>
                    </p>
                    {!hasDates && (
                        <p className={`${styles.summaryLine} ${styles.summaryWarning}`}>
                            Life dates not set. Set in memorial editor.
                        </p>
                    )}

                    <p className={styles.summaryPrice}>
                        Total:
                        {" "}
                        <span className={styles.priceHighlight}>
                            $
                            {price}
                        </span>
                        {" "}
                        <span className={styles.shippingNote}>+ shipping</span>
                    </p>

                    <button
                        className={styles.ctaButton}
                        onClick={handlePurchaseClick}
                    >
                        Buy Candle
                    </button>
                    <p className={styles.summaryNote}>
                        Order quantity can be adjusted at checkout
                    </p>
                    <p className={styles.faqLinkCentered}>
                        <a
                            href={`${getRootDomain()}/forever-candle-faq`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Questions? Read the FAQ →
                        </a>
                    </p>
                </div>
            </section>
            <ConfirmationPopup
                visible={showMissingDatesPopup}
                onCancel={() => setShowMissingDatesPopup(false)}
                onConfirm={() => {
                    setShowMissingDatesPopup(false);
                    void handlePurchase();
                }}
                message="One or more memorial dates are missing. No dates will be included in the engraving. Do you want to continue with the order anyway?"
                confirmButtonText="Continue"
                variant="primary"
            />
        </div>
    );
};

export default PurchaseCandlePage;
